import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'

import { useAPIGetObservationImplementation } from 'api/kinerja/useAPIKinerja'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
} from 'app/KinerjaPage/components/TablePelaksanaanKinerja'
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
} from 'app/KinerjaPage/pages/PelaksanaanV2/components/Card'
import { OBSERVATION_RESULT_BADGE } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/observationResult'

import type { MultiPeriodParams } from 'types/kinerja'
import type { ReactNode } from 'react'

type CekHasilTindakLanjutProps = {
  periodParams?: MultiPeriodParams
  ptkId?: string
  nextButtonText?: ReactNode
  onNext?: () => void
}

export function CekHasilTindakLanjut({
  periodParams,
  ptkId,
  nextButtonText = 'Lanjut',
  onNext,
}: CekHasilTindakLanjutProps) {
  const { data, isLoading } = useAPIGetObservationImplementation(
    { ptkId, ...periodParams },
    {
      useErrorBoundary: true,
    }
  )

  const renderShimmer = () => {
    return Array(3)
      .fill('')
      .map((_x, i) => (
        <TableRow key={i}>
          <TableRowItem className="text-center">
            <Shimmer className="!w-5" />
          </TableRowItem>
          <TableRowItem className="w-5/12">
            <Shimmer className="!w-full" />
          </TableRowItem>
          <TableRowItem>
            <Shimmer />
          </TableRowItem>
          <TableRowItem>
            <Shimmer />
          </TableRowItem>
        </TableRow>
      ))
  }

  return (
    <Card>
      <CardHeader>
        <Text variant="heading-lg" as="h2">
          Hasil Observasi Praktik Kinerja
        </Text>
      </CardHeader>

      <CardBody className="py-6 pl-10 pr-4 space-y-6">
        <Text>
          <strong>Perlu diperhatikan bahwa:</strong>
          <br />
          Hasil Observasi hanya ditujukan untuk memandu diskusi Rencana Tindak
          Lanjut.
        </Text>

        <TablePelaksanaanKinerja>
          <TableHeader>
            <TableHeaderItem>No.</TableHeaderItem>
            <TableHeaderItem>Target Perilaku</TableHeaderItem>
            <TableHeaderItem>Rating</TableHeaderItem>
            <TableHeaderItem>Catatan</TableHeaderItem>
          </TableHeader>

          <TableBody>
            {isLoading
              ? renderShimmer()
              : data?.data?.detail?.behavioralTargets?.map((item, i) => (
                  <TableRow key={item.id}>
                    <TableRowItem className="text-center">{i + 1}</TableRowItem>
                    <TableRowItem className="w-5/12 overflow-wrap-anywhere">
                      {item.behavioralTarget}
                    </TableRowItem>
                    <TableRowItem>
                      <Badge
                        variant={
                          OBSERVATION_RESULT_BADGE[item.observationResult]
                            ?.variant ?? 'neutral'
                        }
                        className="whitespace-nowrap"
                      >
                        {OBSERVATION_RESULT_BADGE[item.observationResult]
                          ?.text ?? '-'}
                      </Badge>
                    </TableRowItem>
                    <TableRowItem className="overflow-wrap-anywhere">
                      {item.observationNote || '-'}
                    </TableRowItem>
                  </TableRow>
                ))}
          </TableBody>
        </TablePelaksanaanKinerja>

        <div className="space-y-1">
          <Text variant="heading-sm">Rekomendasi</Text>
          <Text color="subdued" className="overflow-wrap-anywhere">
            {isLoading ? (
              <Shimmer className="!w-96" />
            ) : (
              data?.data?.detail?.superiorRecommendation || '-'
            )}
          </Text>
        </div>
      </CardBody>

      <CardFooter className="justify-end">
        <Button color="blue" size="lg" onClick={onNext}>
          {nextButtonText}
        </Button>
      </CardFooter>
    </Card>
  )
}
